import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Button,
  CircularProgress,
  TextField,
  Modal,
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Editor } from '@tinymce/tinymce-react';
import { toast } from "react-toastify";
import EmailPreview from "../parts/emailPreview";
import { API_URL, AVATARS_BUCKET } from "../../config";
import defaultLogo from "../../media/shieldshot_logo_white.png";

const SettingsTemplates = ({ data, isLoading, metadata, user, token, templatesByName }) => {
  const isMountedRef = useRef(true);

  // Initialize state with safe defaults
  const initialTemplate = data && data.length > 0 ? data[0] : ['default', '<p></p>'];
  const initialTemplatesByName = templatesByName || ['default'];

  const [templates, setTemplates] = useState(data || [initialTemplate]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [templateName, setTemplateName] = useState(initialTemplate[0]);
  const [oldTemplateName, setOldTemplateName] = useState(initialTemplate[0]);
  const [oldName, setOldName] = useState(initialTemplate[0]);
  const [username, setUsername] = useState(metadata?.user_metadata?.username || '');
  const [body, setBody] = useState(initialTemplate[1]);
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplatesByName[0]);
  const [showModal, setShowModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState("");
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [templatesData, setTemplatesData] = useState(initialTemplatesByName);
  const [showPreview, setShowPreview] = useState(false);
  const [logoUrl, setLogoUrl] = useState(defaultLogo);
  const [avatarUrl, setAvatarUrl] = useState(`https://${AVATARS_BUCKET}.s3.amazonaws.com/${user?.sub}.png` || `https://${AVATARS_BUCKET}.s3.amazonaws.com/default.png`);
  const [lastFunction, setLastFunction] = useState("");

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (data && data.length > 0) {
      setTemplates(data);
      setTemplateName(data[0][0]);
      setOldTemplateName(data[0][0]);
      setOldName(data[0][0]);
      setBody(data[0][1]);
    }
    if (templatesByName && templatesByName.length > 0) {
      setTemplatesData(templatesByName);
      setSelectedTemplate(templatesByName[0]);
    }
    if (metadata?.user_metadata?.username) {
      setUsername(metadata.user_metadata.username);
    }
  }, [data, templatesByName, metadata]);

  const addTemplate = async () => {
    if (!newTemplate.trim()) {
      toast.warning("Template name cannot be empty!");
      return;
    }

    try {
      setCreating(true);
      const response = await fetch(API_URL + "/users/v1/templates/add", {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newTemplate,
          message: "<p></p>",
        }),
        method: "POST",
      });

      if (response.ok) {
        toast.success("New template created");
        setShowModal(false);
        setCreating(false);
        setNewTemplate("");
        setSelectedTemplate(newTemplate);
        setTemplateName(newTemplate);
        setOldTemplateName(newTemplate);
        setLastFunction("add");
        setBody("<p></p>");
        await getTemplates();
      } else {
        const errorData = await response.json();
        toast.error(`Failed to create template: ${errorData.message || 'Unknown error'}`);
        setCreating(false);
      }
    } catch (err) {
      console.error('Add template error:', err);
      toast.error("Failed to create template: " + err.message);
      setCreating(false);
    }
  };

  const deleteTemplate = async () => {
    if (templates.length <= 1) {
      toast.warning("Cannot delete the last template!");
      return;
    }

    try {
      setDeleting(true);
      const response = await fetch(API_URL + "/users/v1/templates/delete", {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: oldTemplateName,
        }),
        method: "POST",
      });

      if (response.ok) {
        toast.success("Template deleted");
        setDeleting(false);
        setLastFunction("delete");
        await getTemplates();
      } else {
        const errorData = await response.json();
        toast.error(`Failed to delete template: ${errorData.message || 'Unknown error'}`);
        setDeleting(false);
      }
    } catch (err) {
      console.error('Delete template error:', err);
      toast.error("Failed to delete template: " + err.message);
      setDeleting(false);
    }
  };

  const updateTemplate = async () => {
    if (updating) return;

    if (!templateName.trim()) {
      toast.warning("Template name cannot be blank!");
      return;
    }

    if (selectedTemplate !== "default" && templateName === "default") {
      toast.warning("You cannot name your template to default!");
      return;
    }

    try {
      const messageBody = body || "<p></p>";
      setUpdating(true);

      const response = await fetch(API_URL + "/users/v1/templates/update", {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          oldName: oldTemplateName,
          newName: templateName,
          message: messageBody,
          type: "templateName",
        }),
        method: "POST",
      });

      if (response.ok) {
        toast.success("Template updated successfully");
        setUpdating(false);
        setLastFunction("update");
        await getTemplates();
      } else {
        const errorData = await response.json();
        toast.error(`Failed to update template: ${errorData.message || 'Unknown error'}`);
        setUpdating(false);
      }
    } catch (err) {
      console.error('Update template error:', err);
      toast.error("Failed to update template: " + err.message);
      setUpdating(false);
    }
  };

  const getTemplates = async () => {
    try {
      const response = await fetch(API_URL + "/users/v1/templates/list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        method: "GET",
      });

      if (response.ok) {
        const responseJson = await response.json();
        const templatesData = Object.entries(responseJson[0].data || {});
        const templateNames = Object.keys(responseJson[0].data || {});

        console.log('Fetched templates:', templatesData);
        console.log('Template names:', templateNames);

        if (isMountedRef.current) {
          setTemplatesData(templateNames);
          setTemplates(templatesData);

          if (lastFunction === "delete" && templatesData.length > 0) {
            setTemplateName(templatesData[0][0]);
            setOldTemplateName(templatesData[0][0]);
            setBody(templatesData[0][1]);
            setSelectedTemplate(templatesData[0][0]);
          }

          if (lastFunction === "update") {
            const index = templateNames.indexOf(templateName);
            if (index !== -1) {
              setOldName(templateName);
              setSelectedTemplate(templateName);
              setBody(templatesData[index][1]);
            }
          }
        }
      } else {
        const errorData = await response.json();
        toast.error(`Failed to fetch templates: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Get templates error:', err);
      toast.error("Failed to fetch templates: Network error");
    }
  };

  const handleTemplateChange = (e) => {
    const selectedName = e.target.value;
    const index = templatesData.indexOf(selectedName);

    if (index !== -1 && templates[index]) {
      setSelectedTemplate(selectedName);
      setTemplateName(selectedName);
      setBody(templates[index][1]);
      setOldTemplateName(selectedName);
    }
  };

  const handleTemplateNameChange = (e) => {
    setTemplateName(e.target.value);
  };

  const handleEditorChange = (content) => {
    setBody(content);
  };

  const handleNewTemplate = (e) => {
    setNewTemplate(e.target.value);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <>
      <Modal
        open={showPreview}
        onClose={() => setShowPreview(false)}
      >
        <Box sx={{ p: 2 }}>
          <EmailPreview
            logoUrl={logoUrl}
            avatarUrl={avatarUrl}
            username={username}
            body={body}
          />
        </Box>
      </Modal>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h6" gutterBottom>
            Add a new Template
          </Typography>
          <TextField
            fullWidth
            placeholder="Type new template name"
            value={newTemplate}
            onChange={handleNewTemplate}
            sx={{ my: 2 }}
          />
          <Button
            variant="contained"
            onClick={addTemplate}
            disabled={creating}
          >
            {creating ? (
              <CircularProgress size={24} />
            ) : (
              "Create Template"
            )}
          </Button>
        </Box>
      </Modal>

      <Paper sx={{ p: 3, textAlign: "left" }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h5">Email Templates</Typography>
            <Typography>Set templates here for distributions.</Typography>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              sx={{ m: 1 }}
              id="previewEmail"
              onClick={() => setShowPreview(!showPreview)}
            >
              Preview Email
            </Button>
            <Button
              variant="contained"
              sx={{ m: 1 }}
              id="addTemplatebutton"
              onClick={() => setShowModal(true)}
            >
              Add a Template
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="select-template-label">
                Select a Template to update
              </InputLabel>
              <Select
                labelId="select-template-label"
                id="selectTemplate"
                value={selectedTemplate}
                label="Select a Template to update"
                onChange={handleTemplateChange}
                disabled={!templates.length}
              >
                {templates.map((template, index) => (
                  <MenuItem value={template[0]} key={index}>
                    {template[0]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Template Name"
              id="templateNameInput"
              onChange={handleTemplateNameChange}
              value={templateName}
              disabled={
                templateName === "default" &&
                oldTemplateName === "default"
              }
            />
          </Grid>
        </Grid>

        <Box sx={{ my: 3 }} id="templateEditor">
          <Editor
            apiKey="er0sqezegew4ztzey6vtavdg77pjxd49j8l34vub9amk48c2"
            value={body}
            init={{
              height: 500,
              menubar: false,
              plugins: [
                "advlist autolink lists link image charmap print preview anchor",
                "searchreplace visualblocks code fullscreen",
                "insertdatetime media table paste code help",
              ],
              toolbar:
                "undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
            }}
            onEditorChange={handleEditorChange}
          />
        </Box>
        <hr />
        <Button
          sx={{ m: 1 }}
          variant="contained"
          id="updateTemplate"
          onClick={updateTemplate}
          disabled={updating}
        >
          {updating ? (
            <CircularProgress size={24} />
          ) : (
            "Update Template"
          )}
        </Button>
        <Button
          sx={{ m: 1 }}
          id="deleteTemplate"
          variant="outlined"
          color="error"
          onClick={deleteTemplate}
          disabled={deleting || templates.length <= 1}
        >
          {deleting ? (
            <CircularProgress size={24} />
          ) : (
            "Delete Template"
          )}
        </Button>
      </Paper>
    </>
  );
};

export default SettingsTemplates;
